import React, { useState, useEffect } from 'react';
import { User, Users, Briefcase, Linkedin, Mail, Star, Award, Heart, UserPlus } from 'lucide-react';
import { t, getCurrentLanguage } from '../utils/translations';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import api from '../components/api';

const EquipePage = () => {
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Écouter les changements de langue
  useEffect(() => {
    // Détecter la langue depuis l'URL au chargement initial
    const detectLanguageFromURL = () => {
      const path = window.location.pathname;
      if (path.startsWith('/en/')) {
        return 'en';
      } else if (path.startsWith('/fr/')) {
        return 'fr';
      }
      return 'fr'; // Par défaut
    };

    const initialLang = detectLanguageFromURL();
    setCurrentLanguage(initialLang);
    
    // Forcer la mise à jour de i18n si nécessaire
    if (window.i18n && window.i18n.language !== initialLang) {
      window.i18n.changeLanguage(initialLang);
      localStorage.setItem('i18nextLng', initialLang);
    }

    const handleLanguageChange = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };

    // Écouter l'événement de changement de langue
    window.addEventListener('languageChanged', handleLanguageChange);

    // Écouter aussi les changements i18n
    const i18nListener = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };

    if (window.i18n) {
      window.i18n.on('languageChanged', i18nListener);
    }

    // Écouter les changements d'URL
    const handleURLChange = () => {
      const urlLang = detectLanguageFromURL();
      if (urlLang !== currentLanguage) {
        setCurrentLanguage(urlLang);
        if (window.i18n && window.i18n.language !== urlLang) {
          window.i18n.changeLanguage(urlLang);
        }
      }
    };

    window.addEventListener('popstate', handleURLChange);

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('popstate', handleURLChange);
      if (window.i18n) {
        window.i18n.off('languageChanged', i18nListener);
      }
    };
  }, [currentLanguage]);

  // Récupérer les données de l'équipe
  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/team/');
        setTeamMembers(response.data);
        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement de l\'équipe');
        console.error('Erreur API Équipe:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamData();
  }, []);

  // Fonction pour obtenir le contenu traduit
  const getTranslatedContent = (frContent, enContent) => {
    if (currentLanguage === 'en' && enContent) {
      return enContent;
    }
    return frContent || enContent || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#089556] mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !teamMembers.length) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex-grow flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Erreur de chargement</h2>
            <p className="text-gray-600 mb-6">{error || 'Aucun membre d\'équipe disponible'}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-[#089556] text-white px-6 py-3 rounded-lg hover:bg-[#078a4a] transition-colors duration-200"
            >
              Réessayer
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Enhanced Hero Section */}
      <section className="relative h-[400px] mb-16 overflow-hidden">
        {/* Enhanced Molecular Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#089556] via-[#0b6f47] to-[#07D1EB]">
          <div className="absolute inset-0 opacity-[0.3]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(255,255,255,0.8) 3px, transparent 3px),
                radial-gradient(circle at 75% 75%, rgba(255,255,255,0.6) 2.5px, transparent 2.5px),
                radial-gradient(circle at 50% 50%, rgba(255,255,255,0.7) 2px, transparent 2px),
                radial-gradient(circle at 80% 30%, rgba(255,255,255,0.5) 3.5px, transparent 3.5px),
                radial-gradient(circle at 20% 70%, rgba(255,255,255,0.8) 2.8px, transparent 2.8px),
                radial-gradient(circle at 60% 90%, rgba(255,255,255,0.6) 2.2px, transparent 2.2px)
              `,
              backgroundSize: '180px 180px, 150px 150px, 200px 200px, 160px 160px, 170px 170px, 140px 140px'
            }}></div>
            
            {/* Enhanced Connection Lines */}
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(45deg, transparent 48%, rgba(255,255,255,0.4) 49%, rgba(255,255,255,0.4) 51%, transparent 52%),
                linear-gradient(-45deg, transparent 48%, rgba(255,255,255,0.3) 49%, rgba(255,255,255,0.3) 51%, transparent 52%),
                linear-gradient(90deg, transparent 48%, rgba(255,255,255,0.35) 49%, rgba(255,255,255,0.35) 51%, transparent 52%)
              `,
              backgroundSize: '300px 300px, 280px 280px, 320px 320px',
              opacity: '0.6'
            }}></div>
          </div>

          {/* Enhanced Floating Molecules */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/5 left-1/5 w-6 h-6 bg-white rounded-full opacity-20 animate-pulse shadow-xl shadow-white/50"></div>
            <div className="absolute top-3/4 right-1/5 w-4 h-4 bg-white rounded-full opacity-30 animate-bounce shadow-xl shadow-white/60"></div>
            <div className="absolute top-1/3 left-4/5 w-5 h-5 bg-white rounded-full opacity-25 animate-pulse shadow-xl shadow-white/55"></div>
            <div className="absolute top-2/3 right-2/5 w-3 h-3 bg-white rounded-full opacity-35 animate-bounce shadow-xl shadow-white/45"></div>
            <div className="absolute bottom-1/4 left-1/3 w-4 h-4 bg-white rounded-full opacity-25 animate-pulse shadow-xl shadow-white/50"></div>
          </div>
        </div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center relative z-10">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-8 shadow-xl border border-white/30">
              <Users className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight drop-shadow-lg mb-4">
              {currentLanguage === 'en' ? 'OUR TEAM' : 'NOTRE ÉQUIPE'}
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed drop-shadow-sm">
              {currentLanguage === 'en' 
                ? 'Dedicated professionals serving humanitarian development' 
                : 'Des professionnels dévoués au service du développement humanitaire'
              }
            </p>
            <div className="w-32 h-1 bg-white/60 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {/* Enhanced Direction Exécutive Section */}
        <section className="relative mb-20 overflow-hidden">
          {/* Molecular Background */}
          <div className="absolute inset-0 opacity-[0.06]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 30% 30%, #089556 2px, transparent 2px),
                radial-gradient(circle at 70% 70%, #07D1EB 1.5px, transparent 1.5px),
                radial-gradient(circle at 50% 90%, #089556 1px, transparent 1px)
              `,
              backgroundSize: '160px 160px, 130px 130px, 190px 190px'
            }}></div>
          </div>

          <div className="relative z-10">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full mb-6 shadow-lg">
                <Briefcase className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-4xl font-bold text-[#089556] mb-4">
                {currentLanguage === 'en' ? 'Executive Leadership' : 'Direction Exécutive'}
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed text-center">
                {currentLanguage === 'en' 
                  ? 'A team of experienced leaders guiding our humanitarian mission' 
                  : 'Une équipe de dirigeants expérimentés guidant notre mission humanitaire'
                }
              </p>
              <div className="w-24 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] mx-auto mt-6 rounded-full"></div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {teamMembers.map((member, index) => {
                const gradients = [
                  "from-blue-500 to-purple-600",
                  "from-green-500 to-teal-600", 
                  "from-cyan-500 to-blue-600",
                  "from-orange-500 to-red-600",
                  "from-indigo-500 to-purple-600",
                  "from-pink-500 to-red-600"
                ];
                
                return (
                  <div key={member.id} className="group relative bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300 overflow-hidden">
                    <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradients[index % gradients.length]} rounded-t-2xl`}></div>
                    
                    <div className="relative">
                      <img 
                        src={member.image_url || member.image} 
                        alt={getTranslatedContent(member.name_fr, member.name_en)}
                        className="w-full h-64 object-cover rounded-xl mb-6 shadow-md group-hover:scale-105 transition-transform duration-300"
                      />
                      
                      <div className="absolute top-4 right-4 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
                        <Star className="h-4 w-4 text-[#089556]" />
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">
                        {getTranslatedContent(member.name_fr, member.name_en)}
                      </h3>
                      <div className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-[#089556]/10 to-[#07D1EB]/10 rounded-full mb-4">
                        <p className="text-[#089556] font-semibold text-sm">
                          {getTranslatedContent(member.position_fr, member.position_en)}
                        </p>
                      </div>
                      <div 
                        className="text-gray-600 text-justify leading-relaxed mb-6"
                        dangerouslySetInnerHTML={{
                          __html: getTranslatedContent(member.bio_fr, member.bio_en)
                        }}
                      />
                      
                      <div className="flex justify-center gap-3">
                        {member.linkedin && (
                          <a 
                            href={member.linkedin} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="group inline-flex items-center justify-center w-10 h-10 bg-[#089556]/10 rounded-full hover:bg-[#089556] transition-all duration-300"
                          >
                            <Linkedin className="h-4 w-4 text-[#089556] group-hover:text-white" />
                          </a>
                        )}
                        {member.email && (
                          <a 
                            href={`mailto:${member.email}`}
                            className="group inline-flex items-center justify-center w-10 h-10 bg-[#07D1EB]/10 rounded-full hover:bg-[#07D1EB] transition-all duration-300"
                          >
                            <Mail className="h-4 w-4 text-[#07D1EB] group-hover:text-white" />
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Decorative Elements */}
                    <div className="absolute bottom-4 left-4 w-8 h-8 opacity-5">
                      <div className="w-2 h-2 bg-[#07D1EB] rounded-full absolute top-0 left-0 animate-pulse"></div>
                      <div className="w-1.5 h-1.5 bg-[#089556] rounded-full absolute top-3 right-0 animate-pulse" style={{animationDelay: '0.5s'}}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Enhanced Nos Valeurs Section */}
        <section className="relative mb-20 overflow-hidden">
          {/* Molecular Background */}
          <div className="absolute inset-0 opacity-[0.05]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 40% 60%, #089556 2px, transparent 2px),
                radial-gradient(circle at 60% 40%, #07D1EB 1.5px, transparent 1.5px)
              `,
              backgroundSize: '170px 170px, 140px 140px'
            }}></div>
          </div>

          <div className="relative z-10 bg-gradient-to-br from-[#089556]/10 via-[#089556]/5 to-[#07D1EB]/10 p-8 rounded-2xl shadow-lg border border-white/20">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full mb-6 shadow-lg">
                <Heart className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-4xl font-bold text-[#089556] mb-4">
                {currentLanguage === 'en' ? 'Our Values' : 'Nos Valeurs'}
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed text-center">
                {currentLanguage === 'en' 
                  ? 'The principles that guide our team in every action' 
                  : 'Les principes qui guident notre équipe dans chaque action'
                }
              </p>
              <div className="w-24 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] mx-auto mt-6 rounded-full"></div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title_fr: "Intégrité",
                  title_en: "Integrity",
                  description_fr: "Nous agissons avec transparence et honnêteté dans toutes nos interventions",
                  description_en: "We act with transparency and honesty in all our interventions",
                  icon: <Award className="h-6 w-6" />,
                  gradient: "from-blue-500 to-purple-600"
                },
                {
                  title_fr: "Solidarité",
                  title_en: "Solidarity",
                  description_fr: "Nous travaillons ensemble pour soutenir les communautés les plus vulnérables",
                  description_en: "We work together to support the most vulnerable communities",
                  icon: <Users className="h-6 w-6" />,
                  gradient: "from-green-500 to-teal-600"
                },
                {
                  title_fr: "Excellence",
                  title_en: "Excellence",
                  description_fr: "Nous visons la qualité dans chaque projet et chaque intervention",
                  description_en: "We aim for quality in every project and every intervention",
                  icon: <Star className="h-6 w-6" />,
                  gradient: "from-orange-500 to-red-600"
                }
              ].map((value, index) => (
                <div key={index} className="bg-white/60 backdrop-blur-sm p-6 rounded-xl shadow-sm border border-white/20 text-center">
                  <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r ${value.gradient} rounded-full mb-4 shadow-lg`}>
                    <div className="text-white">{value.icon}</div>
                  </div>
                  <h4 className="text-xl font-bold text-[#089556] mb-3">
                    {getTranslatedContent(value.title_fr, value.title_en)}
                  </h4>
                  <p className="text-gray-700 text-justify leading-relaxed">
                    {getTranslatedContent(value.description_fr, value.description_en)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Enhanced Bénévoles Section */}
        <section className="relative mb-20 overflow-hidden">
          {/* Molecular Background */}
          <div className="absolute inset-0 opacity-[0.08]">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                radial-gradient(circle at 20% 80%, #089556 2px, transparent 2px),
                radial-gradient(circle at 80% 20%, #07D1EB 1.5px, transparent 1.5px)
              `,
              backgroundSize: '150px 150px, 120px 120px'
            }}></div>
          </div>

          <div className="relative z-10 text-center py-12 bg-gradient-to-br from-[#089556]/15 via-[#089556]/10 to-[#07D1EB]/15 rounded-2xl shadow-lg border border-white/20">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full mb-8 shadow-xl">
              <UserPlus className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-[#089556] mb-6">
              {currentLanguage === 'en' ? 'Join Our Team' : 'Rejoignez Notre Équipe'}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed text-justify mb-8">
              {currentLanguage === 'en' 
                ? 'We are constantly looking for passionate and committed people who share our vision of a more just and united world. Whether you are an experienced professional or a motivated young graduate, your contribution can make a difference in the lives of thousands of people.'
                : 'Nous recherchons constamment des personnes passionnées et engagées qui partagent notre vision d\'un monde plus juste et solidaire. Que vous soyez professionnel expérimenté ou jeune diplômé motivé, votre contribution peut faire la différence dans la vie de milliers de personnes.'
              }
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
              <div className="bg-white/60 backdrop-blur-sm p-6 rounded-xl shadow-sm border border-white/20">
                <User className="h-8 w-8 text-[#089556] mx-auto mb-3" />
                <h4 className="text-lg font-semibold text-[#089556] mb-2">
                  {currentLanguage === 'en' ? 'Volunteers' : 'Bénévoles'}
                </h4>
                <p className="text-gray-700 text-sm text-justify">
                  {currentLanguage === 'en' 
                    ? 'Get involved occasionally in our missions and contribute according to your availability'
                    : 'Engagez-vous ponctuellement dans nos missions et contribuez selon vos disponibilités'
                  }
                </p>
              </div>
              <div className="bg-white/60 backdrop-blur-sm p-6 rounded-xl shadow-sm border border-white/20">
                <Briefcase className="h-8 w-8 text-[#089556] mx-auto mb-3" />
                <h4 className="text-lg font-semibold text-[#089556] mb-2">
                  {currentLanguage === 'en' ? 'Professionals' : 'Professionnels'}
                </h4>
                <p className="text-gray-700 text-sm text-justify">
                  {currentLanguage === 'en' 
                    ? 'Join our permanent team and dedicate your career to humanitarian work'
                    : 'Rejoignez notre équipe permanente et dédiez votre carrière à l\'humanitaire'
                  }
                </p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white font-semibold rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300">
                <UserPlus className="w-5 h-5 mr-3" />
                {currentLanguage === 'en' ? 'Become a volunteer' : 'Devenir bénévole'}
              </button>
              <button className="inline-flex items-center px-8 py-4 bg-white/80 backdrop-blur-sm text-[#089556] font-semibold rounded-xl border-2 border-[#089556]/20 hover:bg-[#089556]/10 hover:scale-105 transition-all duration-300">
                <Briefcase className="w-5 h-5 mr-3" />
                {currentLanguage === 'en' ? 'View open positions' : 'Voir les postes ouverts'}
              </button>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-6 right-6 w-12 h-12 opacity-10">
              <div className="w-3 h-3 bg-[#07D1EB] rounded-full absolute top-0 left-0 animate-pulse"></div>
              <div className="w-2 h-2 bg-[#089556] rounded-full absolute top-3 right-0 animate-pulse" style={{animationDelay: '0.5s'}}></div>
              <div className="w-2.5 h-2.5 bg-[#07D1EB] rounded-full absolute bottom-0 left-3 animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default EquipePage;
